import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import type DiabguideRecommendationComputedJournalEvent from "parog-web/models/diabguide-recommendation-computed-journal-event";
import JournalEvent from "parog-web/models/journal-event";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type MrcStringEnum = "true" | "false" | "unknown";

type RacStringEnum = "under_30" | "between_30_and_300" | "over_300";

type RetiStringEnum = "true" | "false" | "unknown";

type NeurStringEnum = "true" | "false" | "unknown";

type McvaStringEnum = "true" | "false" | "unknown";

type IcStringEnum = "true" | "false" | "unknown";

type FevgStringEnum = "under_40" | "over_40" | "unknown";

type OthStringEnum = "true" | "false" | "unknown";

type HtaStringEnum = "yes" | "no" | "treated" | "unknown";

export default class DiabguideRecommendationStartedJournalEvent extends JournalEvent {
  static type = "diabguide-recommendation-started-journal-event";

  /**
   * age of the patient when call diabguide
   */
  @attr("number") declare age: number;

  /**
   * State of the patient, only if the patient's age > 75, 1 = good health, 2 = fragile, 3 = dependent
   */
  @attr("number") declare state: number;

  /**
   * Height of the patient
   */
  @attr("number") declare height: number;

  /**
   * Weight if the patient
   */
  @attr("number") declare weight: number;

  /**
   * year of the diabete diagnostic
   */
  @attr("number") declare year: number;

  /**
   * last HBA1C known
   */
  @attr("number") declare hba: number;

  /**
   * Has MRC (all stade included)
   */
  @attr("string") declare mrc: MrcStringEnum;

  /**
   * DFG, only if have MRC = true
   */
  @attr("number") declare dfg: number;

  /**
   * albuminurie/creatinurie ratio, only if mrc  = true
   */
  @attr("string") declare rac: RacStringEnum;

  /**
   * has retinopathie
   */
  @attr("string") declare reti: RetiStringEnum;

  /**
   * has neuropathie
   */
  @attr("string") declare neur: NeurStringEnum;

  /**
   * has atheromatous disease
   */
  @attr("string") declare mcva: McvaStringEnum;

  /**
   * has heart failure
   */
  @attr("string") declare ic: IcStringEnum;

  /**
   * ejection fraction of the patient
   */
  @attr("string") declare fevg: FevgStringEnum;

  /**
   * has other severe comorbidity
   */
  @attr("string") declare oth: OthStringEnum;

  /**
   * has life expectation over 5 year
   */
  @attr("boolean", {
    allowNull: true
  })
  declare exp: boolean;

  /**
   * has high blood pressure
   */
  @attr("string") declare hta: HtaStringEnum;

  /**
   * take merfomine
   */
  @attr("boolean", {
    allowNull: true
  })
  declare met: boolean;

  @belongsTo("diabguide-recommendation-computed-journal-event", {
    async: false,
    inverse: "diabguideRecommendationStartedJournalEvent"
  })
  declare diabguideRecommendationComputedJournalEvent: DiabguideRecommendationComputedJournalEvent;

  static validations(intl: IntlService) {
    return {
      age: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.age"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.age"
          )
        })
      ],
      height: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.height"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.height"
          )
        })
      ],
      weight: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.weight"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.weight"
          )
        })
      ],
      year: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.year"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.year"
          )
        })
      ],
      hba: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.hba"
          )
        }),
        validateNumber({
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.hba"
          )
        })
      ],
      mrc: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.mrc"
          )
        }),
        validateInclusion({
          list: ["true", "false", "unknown"],
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.mrc"
          )
        })
      ],
      reti: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.reti"
          )
        }),
        validateInclusion({
          list: ["true", "false", "unknown"],
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.reti"
          )
        })
      ],
      neur: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.neur"
          )
        }),
        validateInclusion({
          list: ["true", "false", "unknown"],
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.neur"
          )
        })
      ],
      mcva: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.mcva"
          )
        }),
        validateInclusion({
          list: ["true", "false", "unknown"],
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.mcva"
          )
        })
      ],
      ic: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.ic"
          )
        }),
        validateInclusion({
          list: ["true", "false", "unknown"],
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.ic"
          )
        })
      ],
      oth: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.oth"
          )
        }),
        validateInclusion({
          list: ["true", "false", "unknown"],
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.oth"
          )
        })
      ],
      hta: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.hta"
          )
        }),
        validateInclusion({
          list: ["yes", "no", "treated", "unknown"],
          description: intl.t(
            "validations.diabguide-recommendation-started-journal-event.hta"
          )
        })
      ],
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.diabguide-recommendation-started-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      state: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.diabguide-recommendation-started-journal-event.state"
        )
      }),
      dfg: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.diabguide-recommendation-started-journal-event.dfg"
        )
      }),
      rac: validateInclusion({
        list: ["under_30", "between_30_and_300", "over_300"],
        allowBlank: true,
        description: intl.t(
          "validations.diabguide-recommendation-started-journal-event.rac"
        )
      }),
      fevg: validateInclusion({
        list: ["under_40", "over_40", "unknown"],
        allowBlank: true,
        description: intl.t(
          "validations.diabguide-recommendation-started-journal-event.fevg"
        )
      })
    };
  }

  //---- End of generated code ----
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "diabguide-recommendation-started-journal-event": DiabguideRecommendationStartedJournalEvent;
  }
}
